import { PlasticsHardwareItem } from '../models/plastics-hardware-item.model';
import { PlasticsInventoryLocationBin } from '../models/plastics-inventory-location.model';

/**
 * Where a plastics component's ON HAND figure actually comes from.
 *
 * There are two counts for the same component and they are not the same number. `qtyOnHand` on the
 * hardware document is a single running total moved by inventory transactions. Once a component is
 * marked INVENTORY CONTROLLED it is instead counted where it physically sits — one bin at a time —
 * and the sum of its bins becomes the truth. The document total is not maintained after that, so it
 * drifts: it is the figure that goes deeply negative while the shelves hold a real, positive count.
 *
 * Every page that states on hand has to make the same choice, and two pages making it differently is
 * exactly the bug this exists to prevent — the browse list read the bins and the inventory manager
 * read the document, so the same component read 123 pcs on one page and -9,329 pcs on the other.
 *
 * An inventory-controlled component with NO bins yet falls back to the document total: it has not
 * been placed on a shelf, so there is nothing to sum, and zero would be a claim rather than an
 * absence. That is why the argument is the bin total or `undefined`, never a bin total defaulted to
 * zero — a component with bins that are genuinely empty really is at 0.
 */
export function resolvePlasticsHardwareQtyOnHand(
    hardwareItem: Pick<PlasticsHardwareItem, 'qtyOnHand' | 'inventoryControlled'>,
    binTotal: number | null | undefined,
): number {
    return hardwareItem.inventoryControlled && binTotal !== null && binTotal !== undefined
        ? binTotal
        : hardwareItem.qtyOnHand;
}

/**
 * Total quantity per hardware item across every bin it occupies. A component with no bins is ABSENT
 * from the result rather than present at 0, which is what lets
 * {@link resolvePlasticsHardwareQtyOnHand} tell "counted on the shelf and empty" apart from "never
 * placed on a shelf".
 */
export function buildPlasticsHardwareBinTotals(bins: PlasticsInventoryLocationBin[]): Map<string, number> {
    const totals = new Map<string, number>();
    for (const bin of bins) {
        if (!bin.hardwareItemId) {
            continue;
        }
        totals.set(bin.hardwareItemId, (totals.get(bin.hardwareItemId) ?? 0) + bin.quantity);
    }
    return totals;
}
